import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import BrandImage from './BrandImage';
import { getSiteProfile } from '../lib/site-profile';
import { getLockedProductImage, getDefaultVariationSelection } from '../lib/variationImageLock';
import { Link } from 'react-router-dom';

/**
 * 7-day rolling high-ticket engagement carousel (weekly_trending_high_ticket).
 */
export default function TrendingCarousel({ siteRole, title = 'Trending This Week' }) {
  const site = siteRole || getSiteProfile().id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [trendingRes, showcaseRes, catalogRes] = await Promise.allSettled([
          axios.get(`${API}/analytics/weekly-trending`, { params: { limit: 12 } }),
          axios.get(`${API}/cheepsweepz/catalog/showcase`, { params: { limit: 12 } }),
          axios.get(`${API}/products`, { params: { destination: 'BB', currency: 'USD', limit: 12 } }),
        ]);

        const catalogItems =
          catalogRes.status === 'fulfilled' && Array.isArray(catalogRes.value.data?.products)
            ? catalogRes.value.data.products
            : [];

        const showcaseItems =
          showcaseRes.status === 'fulfilled' && Array.isArray(showcaseRes.value.data?.items)
            ? showcaseRes.value.data.items
            : [];
        const trendingItems =
          trendingRes.status === 'fulfilled' && Array.isArray(trendingRes.value.data?.items)
            ? trendingRes.value.data.items
            : [];

        const merged = [];
        const seen = new Set();
        for (const item of [...catalogItems, ...showcaseItems, ...trendingItems]) {
          const key = item.product_sku || item.product_id || item.name;
          if (!key || seen.has(key)) continue;
          seen.add(key);
          merged.push(item);
        }

        if (active) setItems(merged.slice(0, 12));
      } catch {
        if (active) setItems([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <section className="py-8">
        <h2 className="mb-4 text-xl font-semibold text-slate-900">{title}</h2>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {[1, 2, 3, 4].map((slot) => (
            <div key={slot} className="h-40 w-56 shrink-0 animate-pulse rounded-2xl bg-slate-200" />
          ))}
        </div>
      </section>
    );
  }

  if (!items.length) return null;

  return (
    <section className="py-8" data-testid="trending-carousel">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        <div className="flex items-center gap-3">
          <Link to="/products" className="text-xs font-semibold uppercase tracking-wide text-cyan-700 hover:text-cyan-900">
            Full catalog
          </Link>
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">7-day pulse</span>
        </div>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item) => {
          const lockedImage =
            getLockedProductImage(item, getDefaultVariationSelection(item?.variation_matrix)) ||
            item.image_url ||
            item.real_images?.[0];
          const productId = item.product_id || item.product_sku;
          return (
          <Link
            key={item.product_sku || item.product_id}
            to={productId ? `/products/${encodeURIComponent(productId)}` : '/products'}
            className="w-56 shrink-0 snap-start overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:border-cyan-300 transition-colors"
          >
            <BrandImage
              siteRole={site}
              alt={item.name || item.product_sku || 'Trending item'}
              className="h-32 w-full"
              src={lockedImage}
              verified={Boolean(item.is_verified_image)}
              showVerifiedBadge={item.image_source === 'google_places'}
            />
            <div className="p-3">
              <p className="truncate text-sm font-semibold text-slate-900">
                {item.name || item.product_sku}
              </p>
              <p className="text-xs text-slate-500">
                {item.image_source === 'google_places'
                  ? 'Google Places · Verified'
                  : `Score ${item.score ?? '—'}`}
              </p>
            </div>
          </Link>
        );})}
      </div>
    </section>
  );
}
